import React, { useState } from 'react';
import useMediaLibraryStore from '@store/media-library-store/media-library.store';
import {
  Button,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PaginationComponent from '@components/pagination/pagination';
import MediaContentItem from './components/media-content-item/media-content-item.tsx';
import { MediaModel } from '@models/media-library.model.ts';
import { getFileType } from '@utils/media-library-utils.ts';
import MediaModal from '@components/media-modal/media-modal.tsx';

const MediaContent: React.FC<{
  mediaKey: string;
  onEditClick: (media: MediaModel) => void;
  onDeleteClick: (media: MediaModel) => void;
}> = ({ mediaKey, onEditClick, onDeleteClick }) => {
  const { t } = useTranslation();
  const [previewFile, setPreviewFile] = useState<MediaModel | null>(null);
  const [previewType, setPreviewType] = useState<any | null>(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const {
    mediaData,
    checkedMediaItems,
    checkedItems,
    pagination,
    toggleSelectAllMedia,
    toggleMediaChecked,
    updatePagination,
    setSelectedMedia,
    selectedMedia
  } = useMediaLibraryStore();

  const [selectedMediaLocal, setSelectedMediaLocal] =
    useState<MediaModel | null>(selectedMedia[mediaKey] || null);

  const disableMedia = Object.values(checkedItems).some(Boolean);
  const allChecked =
    Object.keys(checkedMediaItems).length > 0 &&
    Object.values(checkedMediaItems).every(Boolean);
  const someChecked =
    Object.values(checkedMediaItems).some(Boolean) && !allChecked;

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    toggleSelectAllMedia(event.target.checked);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    updatePagination(value, pagination.rowsPerPage);
    setSelectedMediaLocal(null);
  };
  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    updatePagination(pagination.page, Number(event.target.value));
    setSelectedMediaLocal(null);
  };

  const handleSelectLocal = (media: MediaModel | null) =>
    setSelectedMediaLocal(media);

  const handleSave = () => {
    if (selectedMediaLocal) {
      setSelectedMedia(mediaKey, { ...selectedMediaLocal });
      setSelectedMediaLocal(null);
    }
  };

  const handleOpenPreview = (media: MediaModel) => {
    setPreviewFile(media);
    setPreviewType(getFileType(media) as any);
    setShowDialog(true);
  };

  const handleDownload = (media: MediaModel) => {
    const link = document.createElement('a');
    link.href = media.path;
    link.download = media.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseModal = () => {
    setShowDialog(false);
    setPreviewFile(null);
    setPreviewType(null);
  };

  return (
    <div className="flex flex-col w-full h-full">
      <FormControlLabel
        disabled={disableMedia}
        className="ml-0! mb-2 w-max!"
        control={
          <Checkbox
            disabled={disableMedia}
            className="primary-checkbox"
            checked={allChecked}
            indeterminate={someChecked}
            onChange={handleSelectAllChange}
          />
        }
        label={
          <span className="text-(--blue-4) text-base ml-1">
            {t('view_controls_select_all')}
          </span>
        }
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-4 gap-3 mt-5 max-lg-xl:grid-cols-4 max-lg-xl:mt-2">
          {mediaData.map((item: MediaModel) => (
            <MediaContentItem
              key={item._id}
              data={item}
              disabled={disableMedia}
              checked={checkedMediaItems[item._id] || false}
              onCheckedChange={(checked) =>
                toggleMediaChecked(item._id, checked)
              }
              isSelected={selectedMediaLocal?._id === item._id}
              onSelect={handleSelectLocal}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
              onPreviewClick={handleOpenPreview}
              onDownloadClick={handleDownload}
            />
          ))}
        </div>
      </div>

      <div className="w-full ml-auto mr-auto sticky bottom-0 bg-(--white)">
        <PaginationComponent
          count={pagination.totalPages}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[6, 12, 24, 48]}
        />

        {selectedMediaLocal && (
          <div className="absolute bottom-0 right-0 flex justify-center items-center">
            <Button
              className="max-h-9!"
              type="button"
              variant="contained"
              onClick={handleSave}
            >
              {t('save')}
            </Button>
          </div>
        )}
      </div>

      <MediaModal
        type={previewType!}
        selectedItem={
          previewFile
            ? {
                src: previewFile.path,
                format: previewFile.mimetype.split('/')[1]
              }
            : null
        }
        showDialog={showDialog}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default MediaContent;
